"use strict"

import React, { useState, useEffect, useReducer, useContext } from "react"
import { FormattedMessage } from "react-intl"
import { Form, Button, Modal } from "react-bootstrap"
import moment from "moment/moment"

import { Required } from "../../../../shared_components/widgets/utils"
import ReactSelectionElement from "../../../../shared_components/widgets/ReactSelectionElement"
import PageLoadingPanel from "../../../../shared_components/widgets/PageLoadingPanel"
import AreYouSureModal from "../../../widgets/AreYouSureModal"
import { RequiredFieldWarning } from "../../../internationalization/LocaleFormatted"
import { displayRef } from "../../../widgets/utils"
import { FermManagementConfigContext } from "../../../../config/FermManagementConfigProvider"
import InputFormField from "../../resources/InputFormField"
import FormLabel from "../../resources/FormLabel"
import RunMetadataForm from "./RunMetadataForm"
import InoculationParents from "./InoculationParents"
import ToggleSwitch from "./ToggleSwitch"
import { getTimestamp, inoFormReducer, toSelectArray } from "./utils"
import ChangeLogTable, { ChangeLogButton } from "../ChangeLogTable"
import RunDetailsDiv from "../../resources/RunDetailsDiv"

// Initial state for all common variables or required input fields
let initialState = {
  isLoading: false,
  willInoculate: true,
  inoculationDate: "",
}

// Initial warning state for required input fields
let initialWarningState = {
  reactorWarn: false,
  inoculationWarn: false,
  nameWarn: false,
  descriptionWarn: false,
  commentWarn: false,
  experimentsWarn: false,
}

const InputField = ({ intl, entry, state, updateFormValue }) => {
  const { id, description, field, initial, warningField, canEdit, InputToggle, ValuePlaceholder } = entry
  return (
    <InputFormField
      intl={intl}
      description={description}
      label={id}
      placeholder={id}
      value={state[field]}
      hasChanged={initial !== state[field]}
      updateFormValue={updateFormValue}
      field={field}
      warning={state[warningField]}
      warningField={warningField}
      canEdit={canEdit}
      InputToggle={InputToggle}
      ValuePlaceholder={ValuePlaceholder}
    />
  )
}

export default function StartRunModal({ intl, show, onHide, isUpdate, onSuccess, run }) {
  const context = useContext(FermManagementConfigContext)
  const {
    addEditRunWarn,
    setAddEditRunWarn,
    setExternalSamples,
    setExternalRuns,
    canInoculateSeparately,
    allReactors = [],
    allSystems = [],
    runNameEditableByDefault,
    ableToEditRunName,
    updateRun,
    inoculateRun,
    siteRunMetadata = {},
    experiments: experimentArr = [],
    getExperimentOptions,
    BIOCONTROL_NAME = "",
    IS_PROJECT_EXPERIMENT_SITE = false,
  } = context
  const [experimentOptions, setExperimentOptions] = useState([])
  useEffect(() => {
    // Filter to present only 'active' experiments or current run experiments
    const runExperiments = run?.experiments ? run.experiments.map((e) => e.id) : null
    const activeExperimentOptions = []
    experimentArr.forEach((e) => {
      const validOptions = (e?.options || []).filter(
        (e) => e.active || (runExperiments && runExperiments.includes(e.id)),
      )
      if (validOptions.length > 0) {
        activeExperimentOptions.push({ ...e, options: validOptions })
      }
    })
    setExperimentOptions(activeExperimentOptions)
  }, [experimentArr, run])

  const onFormSubmit = isUpdate ? updateRun : inoculateRun
  const normalizeMetadataValue = (value, menuType) => {
    if (menuType === "multi-dropdown") {
      return toSelectArray(value)
    }

    return value || ""
  }

  const needInoculationInfo =
    (run?.missing_parental_information && canInoculateSeparately) || run?.missing_parental_information

  // Site-specific extra input fields to prompt for a site
  const [extraMetadata, setExtraMetadata] = useState({})
  const [runMetadata, setRunMetadata] = useState({})
  const [requiredMetadata, setRequiredMetadata] = useState([])
  const [activeMetadataFields, setActiveMetadataField] = useState({})
  useEffect(() => {
    const extraMeta = {}
    const runMeta = {}
    const reqMeta = []
    const activeMeta = {}
    Object.entries(siteRunMetadata).forEach(([field, info]) => {
      // Filter on if field is either currently in use OR was when this run was created and the
      // run has a valid entry in metadata for this field
      if (run?.metadata?.[field] || !info?.archived_field) {
        activeMeta[field] = info
        extraMeta[field] = normalizeMetadataValue(info?.default_value, info?.menu_type)
        if (info?.required) {
          const warnField = `${field}Warn`
          initialWarningState[warnField] = false
          reqMeta.push({ field: field, warningField: warnField, canEdit: true })
        }
        if (run) {
          // Populate form with existing Run details or empty placeholders for new (to Run) fields
          runMeta[field] = normalizeMetadataValue(run?.metadata?.[field], info?.menu_type)
        }
      }
    })
    setExtraMetadata(extraMeta)
    setRunMetadata(runMeta)
    setRequiredMetadata(reqMeta)
    setActiveMetadataField(activeMeta)
  }, [siteRunMetadata])

  useEffect(() => {
    // Make sure the state passed to RunMetadataForm is up-to-date. Early load clicks
    // without this can lead to the latest state not being available in the form.
    clearFields()
  }, [runMetadata])

  // Include site-specific fields in setting of an initial state and variable creation
  if (run) {
    initialState = {
      ...initialState,
      experiments: getExperimentOptions(run.experiments || [])
        .map((e) => e.options)
        .flat(),
      run_description: run.run_description || "",
      run_comment: run.run_comment || "",
      name: run.name || "",
      runs: (run.runParents || []).map((rp) => ({
        count: 1,
        complete: true,
        instance: rp,
      })),
      stocks: (run.bioCatalystParents || []).map((bc) => ({
        count: bc.count,
        complete: true,
        instance: {
          ...bc,
          sku: {
            name: bc.name,
          },
        },
      })),
      external_samples: (run.externalSamples || []).map((es) => ({
        count: 1,
        complete: true,
        instance: {
          ...es,
        },
      })),
      external_runs: (run.externalRuns || []).map((er) => ({
        count: 1,
        complete: true,
        instance: {
          ...er,
        },
      })),
      isPlaceholderRun: run.placeholder || false,
      inoculationDate: getTimestamp(run.inoculated_on) || "",
      ...runMetadata,
    }
  } else {
    initialState = {
      ...initialState,
      experiments: [],
      reactor: null,
      stocks: [],
      runs: [],
      external_samples: [],
      external_runs: [],
      name: "",
      run_description: "",
      run_comment: "",
      isPlaceholderRun: false,
      ...extraMetadata,
    }
  }

  const [state, dispatch] = useReducer(inoFormReducer, {
    ...initialState,
    ...initialWarningState,
  })

  const {
    reactor,
    stocks,
    runs,
    external_samples,
    external_runs,
    name,
    nameWarn,
    run_description,
    run_comment,
    isLoading,
    reactorWarn,
    inoculationWarn,
    isPlaceholderRun,
    willInoculate,
    inoculationDate,
    experiments,
    experimentsWarn,
  } = state

  const [currentReactor, setCurrentReactor] = useState({})
  useEffect(() => {
    const sources = [...allSystems, ...allReactors]
    if (run?.id) {
      const r = sources.find((r) => run.reactor === r.name) || {}
      initialState.reactor = r?.id
      setCurrentReactor(r?.id ? r : null)
      updateFormValue(null, "reactor", r?.id, "reactorWarn")
    } else {
      setCurrentReactor(sources.find((item) => reactor === item.id) || null)
    }
  }, [allReactors, allSystems, reactor])

  const clearFields = () => {
    dispatch({ type: "clear", value: { ...initialState, ...initialWarningState } })
    setAddEditRunWarn(false)
    setIsEditingRunName(ableToEditRunName ? runNameEditableByDefault : false)
  }

  const submitFields = () => {
    dispatch({ type: "submit", value: initialWarningState })
  }

  const updateFormValue = (e, field, value, warningField, idx) => {
    e && e.preventDefault()
    setAddEditRunWarn(false)
    dispatch({ type: "field", field: field, value: value, index: idx })
    if (warningField && [warningField]) {
      dispatch({ type: "field", field: warningField, value: false, index: idx })
    }
  }

  const [updateMode, setUpdateMode] = useState(!!run)
  useEffect(() => {
    setUpdateMode(!!run?.id)
  }, [run])

  const [availableReactors, setAvailableReactors] = useState([])
  const [data, setData] = useState({})

  useEffect(() => {
    const availReactors = allReactors.filter((reactor) => !reactor.live)
    if (!updateMode && reactor && !availReactors.find((l) => reactor === l.id)) {
      updateFormValue(null, "reactor", null, "reactorWarn")
    }
    setAvailableReactors(availReactors)
  }, [allReactors])

  const [verifySubmitModal, setVerifySubmitModal] = useState(false)
  const [isEditingRunName, setIsEditingRunName] = useState(true)

  useEffect(() => {
    setIsEditingRunName(ableToEditRunName ? runNameEditableByDefault : false)
  }, [])

  useEffect(() => {
    const reactorList = isPlaceholderRun
      ? availableReactors.concat(allSystems)
      : allReactors.filter((reactor) => !reactor.live)
    setAvailableReactors(reactorList.sort((a, b) => a["name"].localeCompare(b["name"])))
  }, [isPlaceholderRun])

  const externalParentReset = () => {
    setExternalSamples([])
    setExternalRuns([])
  }

  const inputFields = [
    {
      id: "graphing.run.identifier",
      description: "Name",
      field: "name",
      warningField: "nameWarn",
      canEdit: isEditingRunName && !updateMode,
      initial: initialState.name,
      InputToggle: (
        <ToggleSwitch
          intl={intl}
          idVal={"Name"}
          value={isEditingRunName}
          show={ableToEditRunName && !updateMode}
          disabled={updateMode}
          labelInfo={{
            id: "fermmanagement.manually.supply.run.name",
            defaultMessage: "Manually supply run name?",
          }}
          onToggle={() => {
            if (name || nameWarn) updateFormValue(null, "name", "", "nameWarn")
            setIsEditingRunName(!isEditingRunName)
          }}
        />
      ),
      ValuePlaceholder:
        !updateMode && !isEditingRunName ? (
          <div
            style={{ padding: "5px", backgroundColor: "var(--lanzalake-blue-bright)", borderRadius: "3px" }}
          >
            <i style={{ color: "var(--lanzalake-grey-mid)" }}>
              {intl.formatMessage({
                id: "fermmanagement.run.name.auto.generated.msg",
                defaultMessage: "The run name will be automatically generated",
              })}
            </i>
          </div>
        ) : null,
    },
    {
      id: "general.description",
      description: "Description",
      field: "run_description",
      warningField: "descriptionWarn",
      canEdit: true,
      initial: initialState.run_description,
      type: "textarea",
    },
    {
      id: "general.comment",
      description: "Comment",
      field: "run_comment",
      warningField: "commentWarn",
      canEdit: true,
      initial: initialState.run_comment,
      type: "textarea",
    },
  ]

  const validateForm = (e) => {
    e.preventDefault()
    submitFields()

    let validSubmission = true
    let missingInfo = []
    const fieldsToValidate = [
      { field: "reactor", warningField: "reactorWarn", canEdit: true },
      ...inputFields,
      ...(isPlaceholderRun ? [] : requiredMetadata),
    ]

    fieldsToValidate
      .filter((field) => field.canEdit)
      .forEach((field) => {
        const value = state[field["field"]]
        const isEmptyValue =
          (Array.isArray(value) && value.length === 0) ||
          value === "" ||
          value === null ||
          value === undefined
        if (isEmptyValue) {
          validSubmission = false
          missingInfo.push(field["warningField"])
          dispatch({ type: "field", field: field["warningField"], value: true })
        }
      })

    const runExperiments = experiments.map((e) => e.id)
    if (IS_PROJECT_EXPERIMENT_SITE && !isPlaceholderRun) {
      // Require at least one experiment selection at certain sites
      if (runExperiments.length === 0) {
        validSubmission = false
        missingInfo.push("experimentsWarn")
        dispatch({ type: "field", field: "experimentsWarn", value: true })
      }
    }

    const completeRuns = runs.filter((r) => r.complete)
    const completeStocks = stocks.filter((r) => r.complete)
    const completeExternalSamples = external_samples.filter((es) => es.complete)
    const completeExternalRuns = external_runs.filter((er) => er.complete)

    if (
      !isPlaceholderRun &&
      willInoculate &&
      ([
        // If no parents selected at all
        ...completeRuns,
        ...completeStocks,
        ...completeExternalSamples,
        ...completeExternalRuns,
      ].length === 0 ||
        [
          // If any parents have warnings
          ...runs.filter((r) => !r.complete),
          ...stocks.filter((r) => !r.complete),
          ...external_samples.filter((es) => !es.complete),
          ...external_runs.filter((er) => !er.complete),
        ].length > 0)
    ) {
      validSubmission = false
      missingInfo.push("inoculationWarn")
      dispatch({ type: "field", field: "inoculationWarn", value: true })
    }
    if (validSubmission) {
      let info = {
        source_id: reactor,
        biocatalyst_parents: completeStocks,
        run_parents: completeRuns,
        external_samples: completeExternalSamples,
        external_runs: completeExternalRuns,
        placeholder: isPlaceholderRun,
        experiments: runExperiments,
        ...(!isPlaceholderRun
          ? {
              metadata:
                Object.keys(activeMetadataFields).reduce((prev, field) => {
                  prev[field] = state[field]
                  return prev
                }, {}) || {},
            }
          : {}),
      }

      inputFields
        .filter((input) => input.canEdit)
        .forEach((input) => (info[input["field"]] = state[input["field"]]))
      if (updateMode && inoculationDate) {
        info["inoculation_date"] = moment(inoculationDate).format("YYYY-MM-DDTHH:mm:ssZZ") // Format to make ts timezone aware when sent to endpoint
      }

      setData(info)
      setVerifySubmitModal(true)
      displayRef("inoculate_run_modal", false)
    } else {
      dispatch({ type: "invalidForm" })
      const msg = `${intl.formatMessage({ id: "general.required.form.fields.empty", defaultMessage: "Please fill out all required form fields" })}: ${missingInfo.join(", ")}`
      setAddEditRunWarn(msg)
      alert(msg)
    }
  }

  const generalFormSubmission = (e) => {
    if (e) {
      e.preventDefault()
    }

    setVerifySubmitModal(false)
    displayRef("inoculate_run_modal", true)
  }

  const submitForm = (e, d) => {
    generalFormSubmission(e)
    submitFields()
    if (updateMode) {
      // Excluding fields from data payload that cannot/should not be updated
      const {
        source_id,
        placeholder,
        name,
        biocatalyst_parents,
        run_parents,
        external_samples,
        external_runs,
        ...runInfo
      } = d
      onFormSubmit(
        run,
        () => dispatch({ type: "invalidForm" }),
        runInfo,
        needInoculationInfo // Only allow setting parental information if none currently present in system
          ? {
              biocatalyst_parents: biocatalyst_parents,
              run_parents: run_parents,
              external_samples: external_samples,
              external_runs: external_runs,
            }
          : null,
        onSuccess,
      )
    } else {
      onFormSubmit(d, () => dispatch({ type: "invalidForm" }), onSuccess)
    }
  }

  const cancelForm = (e) => {
    generalFormSubmission(e)
    dispatch({ type: "invalidForm" })
  }

  const inputFieldProps = {
    intl: intl,
    state: state,
    updateFormValue: updateFormValue,
  }

  const [showChangeLog, setShowChangeLog] = useState(false)
  const getBiocontrolParents = (parents = []) => {
    return parents.map((p) => ({
      name: `${p?.instance?.name || p?.instance?.id}`,
      name_source: p?.instance?.name_source || BIOCONTROL_NAME,
    }))
  }

  return (
    <>
      {verifySubmitModal && (
        <AreYouSureModal
          show={verifySubmitModal}
          onHide={(e) => cancelForm(e)}
          data={data}
          modalBody={
            <RunDetailsDiv
              intl={intl}
              selectedRun={{
                reactor: (currentReactor || {}).name,
                run_description: run_description,
                run_comment: run_comment,
                placeholder: isPlaceholderRun,
                metadata: data?.metadata || {},
                runParents: data.run_parents.map((p) => p.instance),
                bioCatalystParents: data.biocatalyst_parents.map((p) => ({
                  name: p?.instance?.sku?.name,
                  lot_no: p?.instance?.lot_no,
                  count: p.count,
                  uom: p?.instance?.uom,
                })),
                experiments: experiments.map((e) => e.obj),
                externalRuns: getBiocontrolParents(data.external_runs),
                externalSamples: getBiocontrolParents(data.external_samples),
                ...(run?.id ? { started_on: run.started_on } : {}),
                ...("inoculation_date" in data ? { inoculated_on: inoculationDate } : {}),
                ...(name ? { name: name } : {}),
              }}
              showParents={willInoculate}
              showLog={false}
              linkRunName={false}
              view={true}
            />
          }
          submitForm={submitForm}
          cancelForm={cancelForm}
        />
      )}
      <Modal
        show={show}
        onHide={() => {
          onHide()
          externalParentReset()
        }}
        backdrop={"static"}
        dialogClassName={"mw-100 wide-modal"}
        style={{ overflowX: "scroll", overflowY: "scroll" }}
        id={"inoculate_run_modal"}
      >
        <Modal.Header closeButton>
          <Modal.Title className={"text-center"}>
            {updateMode ? (
              <>{`${intl.formatMessage({ id: "general.edit", defaultMessage: "Edit" })}: ${run.name}`}</>
            ) : (
              <FormattedMessage id={"fermmanagement.start.run"} defaultMessage={"Start Run"} />
            )}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {isLoading ? (
            <PageLoadingPanel />
          ) : (
            <>
              {addEditRunWarn && (
                <p style={{ marginTop: "10px", marginBottom: "20px" }} className={"error-message"}>
                  <FormattedMessage id={"general.server.error"} description={"Server Error Message"} />:{" "}
                  {addEditRunWarn}
                </p>
              )}
              <Form id={"inoculateRunForm"}>
                <div style={{ display: "flex" }}>
                  <ToggleSwitch
                    intl={intl}
                    idVal={"placeholderToggle"}
                    value={isPlaceholderRun}
                    show={true}
                    disabled={updateMode}
                    labelInfo={{
                      id: "graphing.run.placeholder",
                      defaultMessage: "Placeholder",
                    }}
                    popoverInfo={{
                      id: "fermmanagement.placeholder.info.msg",
                      defaultMessage:
                        "Toggle to create a non-fermentation run (a run without an inoculation source or parent)",
                    }}
                    onToggle={() => {
                      clearFields()
                      dispatch({ type: "field", field: "isPlaceholderRun", value: !isPlaceholderRun })
                    }}
                    toggleStyle={{ marginBottom: "5px" }}
                  />
                  {(run?.change_logs || []).length > 0 && (
                    <ChangeLogButton
                      intl={intl}
                      onClick={() => setShowChangeLog(!showChangeLog)}
                      isExpanded={showChangeLog}
                    />
                  )}
                </div>
                {showChangeLog && <ChangeLogTable intl={intl} changeLogs={run?.change_logs || []} />}
                <div
                  className={isPlaceholderRun ? "" : "row"}
                  style={{ marginBottom: "0", marginTop: "10px" }}
                >
                  <div className={isPlaceholderRun ? "row" : "col-md-5"}>
                    {!isPlaceholderRun && IS_PROJECT_EXPERIMENT_SITE && (
                      <div>
                        <ReactSelectionElement
                          label={intl.formatMessage({
                            id: "general.project.experiments",
                            defaultMessage: "Experiments",
                          })}
                          boldLabel={true}
                          required={IS_PROJECT_EXPERIMENT_SITE}
                          isMulti={true}
                          defaultValue={experiments}
                          value={experiments}
                          onValueChange={(_, selected) => {
                            updateFormValue(null, "experiments", selected, "experimentsWarn")
                          }}
                          list={experimentOptions}
                          formatOptionLabel={({ label, project, task }) => (
                            <div>
                              <div style={{ fontWeight: "bold" }}>{label}</div>
                              {[
                                [{ id: "general.project", defaultMessage: "Project" }, project],
                                [{ id: "general.project.task", defaultMessage: "Task" }, task],
                              ].map((detail, idx) => (
                                <div
                                  key={`${idx}${label}`}
                                  style={{ fontSize: "0.9em", fontStyle: "italic" }}
                                >
                                  <label style={{ margin: "0" }}>{intl.formatMessage(detail[0])}</label>:
                                  &nbsp;
                                  {detail[1]}
                                </div>
                              ))}
                            </div>
                          )}
                          filterOption={(option = {}, rawInput) => {
                            const input = rawInput.toLowerCase()
                            const { value = "", task = "", project = "" } = option.data || {}
                            const strContainStr = (str) => str.toLowerCase().includes(input)
                            return strContainStr(value) || strContainStr(task) || strContainStr(project)
                          }}
                        />
                        <RequiredFieldWarning warning={experimentsWarn} />
                      </div>
                    )}
                    <div className={isPlaceholderRun ? " col-md-6" : ""}>
                      <ReactSelectionElement
                        name={"reactor"}
                        label={intl.formatMessage({ id: "general.reactor", defaultMessage: "Reactor" })}
                        boldLabel={true}
                        placeholder={`-- ${intl.formatMessage({
                          id: "general.dropdown.choose.reactor",
                          defaultMessage: "Choose Reactor",
                        })} --`}
                        list={availableReactors}
                        getOptionLabel={(obj) => obj.name}
                        getOptionValue={(obj) => obj.id}
                        value={currentReactor}
                        hasChanged={initialState.reactor !== reactor}
                        defaultValue={currentReactor}
                        onValueChange={(field, opt) =>
                          updateFormValue(null, field, opt?.id || null, "reactorWarn")
                        }
                        disabled={availableReactors.length === 0 || isLoading || updateMode}
                        isMulti={false}
                      />
                      <RequiredFieldWarning warning={reactorWarn} />
                    </div>
                    <div className={isPlaceholderRun ? "col-md-6" : ""}>
                      <InputField entry={inputFields[0]} {...inputFieldProps} />
                    </div>
                  </div>

                  {!isPlaceholderRun && (
                    <div className={"col-md-7"}>
                      <div style={{ display: "flex" }}>
                        <div>
                          <FormLabel description={"Inoculum"} label={"fermmanagement.inoculum"} />
                          {willInoculate && <Required />}
                        </div>
                        <div style={{ marginLeft: "auto" }}>
                          <ToggleSwitch
                            intl={intl}
                            idVal={"willInoculateToggle"}
                            value={willInoculate}
                            show={!updateMode || needInoculationInfo}
                            disabled={!canInoculateSeparately}
                            labelInfo={{
                              id: "fermmanagement.inoculate.run",
                              defaultMessage: "Inoculate Run",
                            }}
                            onToggle={() => {
                              const parentFields = ["stocks", "runs", "external_samples", "external_runs"]
                              parentFields.forEach((f) => dispatch({ type: "field", field: f, value: [] }))
                              if (inoculationWarn) {
                                dispatch({ type: "field", field: "inoculationWarn", value: false })
                              }
                              dispatch({ type: "field", field: "willInoculate", value: !willInoculate })
                            }}
                          />
                        </div>
                      </div>
                      {willInoculate ? (
                        <InoculationParents
                          intl={intl}
                          isLoading={isLoading}
                          run_name={run?.name}
                          started_on={getTimestamp(run?.started_on)}
                          inoculated_on={inoculationDate}
                          runs={runs}
                          stocks={stocks}
                          external_samples={external_samples}
                          external_runs={external_runs}
                          canEditParentalInfo={(!updateMode || needInoculationInfo) && !isLoading}
                          updateField={(f, s, v, i, type = "removeRow") => {
                            if (type === "removeRow") {
                              dispatch({
                                type: "removeRow",
                                field: f,
                                index: i,
                              })
                            } else if (type === "row") {
                              dispatch({ type: "row", field: f })
                            } else if (type === "field") {
                              dispatch({ type: "field", field: f, value: v })
                            } else {
                              dispatch({
                                type: "listField",
                                field: f,
                                sub_field: s,
                                value: v,
                                index: i,
                              })
                            }
                            if (inoculationWarn) {
                              dispatch({ type: "field", field: "inoculationWarn", value: false })
                            }
                          }}
                        />
                      ) : (
                        <div style={{ padding: "10px", backgroundColor: "var(--lanzalake-blue-bright)" }}>
                          <i style={{ color: "var(--lanzalake-grey-mid)" }}>
                            {intl.formatMessage({
                              id: "fermmanagement.will.inoculate.later",
                              defaultMessage: "The run will be inoculated at a later date",
                            })}
                          </i>
                        </div>
                      )}
                      <RequiredFieldWarning warning={inoculationWarn} />
                    </div>
                  )}
                </div>
                <div className={"row"} style={{ marginBottom: "0", marginTop: "10px" }}>
                  <div className={"col-6"}>
                    <InputField entry={inputFields[1]} {...inputFieldProps} />
                  </div>
                  <div className={"col-6"}>
                    <InputField entry={inputFields[2]} {...inputFieldProps} />
                  </div>
                </div>
                <RunMetadataForm
                  intl={intl}
                  state={state}
                  updateFormValue={updateFormValue}
                  activeMetadataFields={activeMetadataFields}
                  initialState={initialState}
                  isPlaceholderRun={isPlaceholderRun}
                />
              </Form>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            style={{ marginRight: "auto" }}
            type={"reset"}
            variant={"outline-primary"}
            disabled={isLoading}
            onClick={(e) => {
              e.preventDefault()
              clearFields()
              onHide()
              externalParentReset()
            }}
          >
            <FormattedMessage
              id={"general.closeButton"}
              description={"Close Button For Inoculate/Edit Run Form"}
            />
          </Button>
          <Button
            style={{ marginLeft: "auto" }}
            type={"reset"}
            variant={"danger"}
            disabled={isLoading}
            onClick={(e) => {
              e.preventDefault()
              clearFields()
              externalParentReset()
            }}
          >
            {intl.formatMessage({ id: "general.reset", defaultMessage: "Reset" })}
          </Button>
          &nbsp;
          <Button type={"submit"} variant={"primary"} disabled={isLoading} onClick={(e) => validateForm(e)}>
            {intl.formatMessage(
              updateMode
                ? { id: "general.update", defaultMessage: "Update" }
                : { id: "general.submitButton", defaultMessage: "Submit" },
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  )
}
